import {twJoin} from 'tailwind-merge'
import {useMemo} from 'react'
import {useRef} from 'react'
import {nanoid} from 'nanoid'

import {onButtonKeyEvent} from '@sdox/utils'

import styles from './tag.module.scss'
import {useDisclaimerDialogStore} from '../../stores'
import {RichTextTooltip} from '../Tooltip/RichTextTooltip'

import type {TooltipRefProps} from 'react-tooltip'

type TagVariants = 'PRIMARY' | 'SECONDARY' | 'TERTIARY' | 'BRAND' | 'INVERSED_PRIMARY' | 'INVERSED_TERTIARY'

export interface TagProps {
  identifier: string
  variant?: TagVariants
  textContent?: string
  textHover?: string
  colorBackground?: string
  colorFont?: string
  colorBorder?: string
}

const variantClasses: Record<TagVariants, string> = {
  PRIMARY: styles.primary,
  SECONDARY: styles.secondary,
  TERTIARY: styles.tertiary,
  BRAND: styles.brand,
  INVERSED_PRIMARY: styles.inversedPrimary,
  INVERSED_TERTIARY: styles.inversedTertiary,
}

export const Tag = (props: TagProps) => {
  const {
    identifier,
    variant = 'PRIMARY',
    textContent,
    textHover,
    colorBorder,
    colorFont,
    colorBackground,
  } = props

  const tooltipRef = useRef<TooltipRefProps>(null)
  const id = useMemo(() => `tag_${nanoid(10)}`, [])
  const setActiveId = useDisclaimerDialogStore(state => state.setActiveId)
  const hasTextHover = textHover && textHover.trim().length > 0

  const customStyles = {
    '--tw-ring-color': colorBackground,
    color: colorFont,
    backgroundColor: colorBackground,
  }

  return (
    <>
      <span
        id={id}
        className={twJoin(
          styles.tag,
          variantClasses[variant],
          hasTextHover && 'cursor-pointer',
        )}
        data-identifier={identifier}
        onClick={(event) => {
          event.stopPropagation()
          setActiveId(identifier)
        }}
        onKeyDown={onButtonKeyEvent((event) => {
          event.stopPropagation()
          setActiveId(identifier)
        })}
        onMouseEnter={() => tooltipRef.current?.open()}
        onMouseLeave={() => tooltipRef.current?.close()}
        style={customStyles}
      >
        {textContent?.slice(0, 40)}
        <span style={{borderColor: colorBorder}}></span>
      </span>
      {hasTextHover ? (
        <RichTextTooltip
          richText={textHover}
          anchorSelect={`#${id}`}
          delay={300}
          position={'top'}
        />
      ) : null}
    </>
  )
}
